import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { UploadZone } from "../components/UploadZone";
import { ColumnMapping } from "../components/ColumnMapping";
import { ParsingProgress } from "../components/ParsingProgress";
import { CurrentDataset } from "../components/CurrentDataset";
import { HolidayManager } from "../components/HolidayManager";
import { AutoImport } from "../components/AutoImport";
import { previewFile, detectColumns, analyzeFile, makeDemoFile } from "../lib/csvAnalysis";
import { fetchMeta, saveDataset, deleteDataset } from "../lib/api";
import { fmtNum } from "../lib/format";

export default function ManagePage() {
  const nav = useNavigate();
  const [stage, setStage] = useState("upload");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState(null);
  const [progress, setProgress] = useState([0, 0]);
  const [meta, setMeta] = useState(undefined);

  useEffect(() => { fetchMeta().then(setMeta).catch(() => setMeta(null)); }, []);

  const loadFile = async (f) => {
    if (!/\.csv$/i.test(f.name) && f.type !== "text/csv") return toast.error("Please upload a .csv file");
    try {
      const p = await previewFile(f);
      setFile(f); setPreview(p); setMapping(detectColumns(p.headers, p.rows)); setStage("mapping");
    } catch (e) { toast.error(e.message || "Could not read file"); }
  };

  const run = async () => {
    setStage("parsing"); setProgress([0, 0]);
    try {
      const res = await analyzeFile(file, mapping, (pct, rows) => setProgress([pct, rows]));
      setProgress([100, res.rowsRead]); setStage("saving");
      await saveDataset({ ...res, fileName: file.name, fileSize: file.size, mapping: { ...mapping } });
      toast.success(`Saved ${fmtNum(res.rowsRead)} rows · ${fmtNum(res.users.length)} users`);
      nav("/");
    } catch (e) { toast.error(e.response?.data?.detail || e.message || "Processing failed"); setStage("mapping"); }
  };

  const remove = async () => {
    await deleteDataset(); setMeta(null); toast.success("Dataset deleted");
  };

  const reset = () => { setStage("upload"); setFile(null); setPreview(null); };

  if (stage === "mapping") return <ColumnMapping file={file} preview={preview} mapping={mapping} setMapping={setMapping} onConfirm={run} onCancel={reset} />;
  if (stage === "parsing" || stage === "saving") return <ParsingProgress file={file} progress={progress[0]} rows={progress[1]} saving={stage === "saving"} />;
  return (
    <div>
      <CurrentDataset meta={meta} onDelete={remove} />
      <UploadZone onFile={loadFile} onDemo={() => loadFile(makeDemoFile())} replacing={!!meta} />
      <HolidayManager />
      <div className="max-w-5xl mx-auto px-6 pb-16"><AutoImport onImported={() => fetchMeta().then(setMeta).catch(() => {})} /></div>
    </div>
  );
}
